import { useState } from "react";
import { User, Lock, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useProcurement } from "@/context/ProcurementContext";
import { mockUsers } from "@/data/mockData";

// Demo user data for quick access
const demoUsers = [
  { username: "admin", name: "System Administrator", role: "admin" },
  { username: "procurement_mgr", name: "Michael Brown", role: "procurement_manager" },
  { username: "qc_inspector", name: "Sarah Johnson", role: "quality_inspector" },
  { username: "approver1", name: "John Smith", role: "approver" },
  { username: "user1", name: "Emily Davis", role: "user" },
];

interface LoginPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin?: (user: any) => void; // Made optional since we're using context now
}

export function LoginPanel({ isOpen, onClose }: LoginPanelProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { state, actions } = useProcurement();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const success = await actions.login(username, password);
    if (success) {
      onClose();
    }
  };

  const handleDemoLogin = async (demoUser: any) => {
    const success = await actions.login(demoUser.username, "demo123"); // All demo users have same password
    if (success) {
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[400px] sm:w-[500px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <LogIn className="h-5 w-5" />
            Login to KKG Electricals
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={state.isLoading}
            >
              {state.isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-surface px-2 text-muted-foreground">Demo Accounts</span>
            </div>
          </div>

          <div className="space-y-3">
            {demoUsers.map((user, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-surface-variant rounded-lg border border-card-border"
              >
                <div className="space-y-1">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">@{user.username}</p>
                  <Badge variant="outline" className="text-xs">
                    {user.role.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDemoLogin(user)}
                  disabled={state.isLoading}
                >
                  Login
                </Button>
              </div>
            ))}
          </div>

          <div className="text-xs text-muted-foreground text-center">
            This is a demo system. Use any of the demo accounts above or the login form.
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}